import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import {
  ArrowLeft, ArrowRight, CheckCircle, Buildings, ShieldCheck, Phone, User,
} from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api } from "@/lib/api";
import { LOGO_URL, COMPANY } from "@/lib/site-data";

const PROPERTY_TYPES = [
  "Apartment Buildings / Multi-Family",
  "Property Management Companies (multiple sites)",
  "Retail Plazas / Stores",
  "Non Profit Organization",
  "Other Commercial",
];

const FREQUENCIES = ["Weekly", "Bi-Weekly", "Monthly", "As-needed", "Not sure yet"];

const CONTRACT_LENGTHS = [
  "Ongoing",
  "Long-term (12+ months)",
  "Fixed-term project",
  "Just exploring options",
];

const inputCls =
  "w-full bg-white border-2 border-slate-900 px-4 py-3 text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-emerald-700 focus:ring-0 transition-colors";

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[11px] font-extrabold tracking-[0.2em] uppercase text-slate-800">
          {label} {required && <span className="text-emerald-700">*</span>}
        </span>
        {hint && <span className="text-[10px] text-slate-400">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

export default function CommercialQuote() {
  const [propertyType, setPropertyType] = useState("");
  const [unitsBuildings, setUnitsBuildings] = useState("");
  const [frequency, setFrequency] = useState("");
  const [contractLength, setContractLength] = useState("");
  const [siteNotes, setSiteNotes] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [propertyName, setPropertyName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    if (!propertyType) return toast.error("Please pick a property type");
    if (!name.trim() || !email.trim() || !phone.trim()) {
      return toast.error("Please fill in your name, email and phone");
    }
    setSubmitting(true);
    try {
      await api.post("/commercial-leads", {
        property_type: propertyType,
        units_or_buildings: unitsBuildings.trim() || null,
        frequency: frequency || null,
        contract_length: contractLength || null,
        site_notes: siteNotes.trim() || null,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        property_name: propertyName.trim() || null,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't submit — please try again or call us.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <Toaster position="top-center" richColors />
      {/* Top bar */}
      <header className="bg-white border-b-2 border-slate-900 sticky top-0 z-30">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Troya" className="h-10 w-10 object-contain" />
            <div className="leading-tight">
              <div className="font-display font-black text-base text-slate-900">TROYA <span className="text-sky-500">MINI BINS</span></div>
              <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-emerald-700">Commercial · Custom Quote</div>
            </div>
          </Link>
          <Link
            to="/"
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2 border-2 border-slate-900 text-slate-900 font-bold text-xs uppercase tracking-[0.18em] hover:bg-slate-900 hover:text-white transition-colors"
          >
            <ArrowLeft size={16} weight="bold" /> Back
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-6 lg:px-8 py-12 lg:py-16">
        {done ? (
          <div className="border-2 border-emerald-700 bg-white" data-testid="commercial-success">
            <div className="bg-emerald-700 text-white px-7 py-7 flex items-center gap-4">
              <CheckCircle size={48} weight="fill" />
              <div>
                <div className="text-[10px] font-extrabold tracking-[0.22em] uppercase text-emerald-100">Got it</div>
                <div className="font-display font-black text-2xl sm:text-3xl leading-tight">
                  We&apos;ll get back to you within 1&ndash;2 business days with your custom quote.
                </div>
              </div>
            </div>
            <div className="p-7 sm:p-10 space-y-5">
              <p className="text-slate-800 leading-relaxed text-lg">
                No forms in a queue, no auto-reply spam. A real person on our team is going to
                read your request, look at your property, and put together pricing that actually
                fits what you&apos;re doing.
              </p>
              <p className="text-slate-700 leading-relaxed">
                Need us moving faster than that? Call{" "}
                <a href={`tel:${COMPANY.phoneRaw}`} className="font-bold text-emerald-700 underline underline-offset-4">
                  {COMPANY.phone}
                </a>{" "}
                and mention this is a commercial job &mdash; we&apos;ll bump you to the top of the list.
              </p>
              <div className="grid sm:grid-cols-3 gap-3 border-t border-slate-200 pt-5">
                <div className="border-2 border-slate-900 bg-white p-4">
                  <div className="text-[9px] font-extrabold tracking-[0.22em] uppercase text-slate-500">Next</div>
                  <div className="font-display font-bold text-slate-900 mt-1">We review your details</div>
                </div>
                <div className="border-2 border-slate-900 bg-white p-4">
                  <div className="text-[9px] font-extrabold tracking-[0.22em] uppercase text-slate-500">Then</div>
                  <div className="font-display font-bold text-slate-900 mt-1">You get a real quote &mdash; not a rate card</div>
                </div>
                <div className="border-2 border-slate-900 bg-white p-4">
                  <div className="text-[9px] font-extrabold tracking-[0.22em] uppercase text-slate-500">Timeline</div>
                  <div className="font-display font-bold text-slate-900 mt-1">1&ndash;2 business days, max</div>
                </div>
              </div>
              <Link
                to="/"
                data-testid="commercial-success-back"
                className="mt-4 inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white font-bold text-xs uppercase tracking-[0.18em] hover:bg-slate-800 transition-colors"
              >
                Back to home
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-10">
              <div className="inline-flex items-center gap-2 border-2 border-emerald-700 bg-emerald-50 px-3 py-1.5 mb-5">
                <Buildings size={14} weight="bold" className="text-emerald-700" />
                <span className="text-[10px] font-extrabold tracking-[0.22em] uppercase text-emerald-700">
                  Commercial &amp; Contract
                </span>
              </div>
              <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-[1.05] tracking-tight">
                Request your custom quote.
              </h1>
              <p className="mt-5 text-slate-700 text-lg leading-relaxed max-w-3xl">
                Tell us about your property and we&apos;ll put together pricing built around your actual
                needs &mdash; no generic rate card, no guesswork. A dedicated account manager will follow
                up within <span className="font-bold text-slate-900">1&ndash;2 business days</span>.
              </p>
            </div>

            <form onSubmit={onSubmit} className="border-2 border-slate-900 bg-white p-7 sm:p-10 space-y-8" data-testid="commercial-form">
              {/* Field 1 - property type */}
              <Field label="What best describes your property?" required>
                <RadioGroup value={propertyType} onValueChange={setPropertyType} data-testid="commercial-property-type">
                  <div className="grid sm:grid-cols-2 gap-3">
                    {PROPERTY_TYPES.map((p) => (
                      <label
                        key={p}
                        className={`flex items-start gap-3 border-2 px-4 py-3 cursor-pointer transition-colors ${
                          propertyType === p ? "border-emerald-700 bg-emerald-50" : "border-slate-900 hover:bg-slate-50"
                        }`}
                      >
                        <RadioGroupItem
                          value={p}
                          id={`pt-${p}`}
                          className="mt-1 border-2 border-slate-900 data-[state=checked]:bg-emerald-700 data-[state=checked]:border-emerald-700"
                        />
                        <span className="text-sm font-semibold text-slate-900">{p}</span>
                      </label>
                    ))}
                  </div>
                </RadioGroup>
              </Field>

              {/* Field 2 - scale */}
              <Field label="How many units or buildings are we talking about?">
                <input
                  data-testid="commercial-units"
                  type="text"
                  value={unitsBuildings}
                  onChange={(e) => setUnitsBuildings(e.target.value)}
                  placeholder="e.g. 48 units / 3 buildings"
                  className={inputCls}
                />
              </Field>

              {/* Field 3 - frequency */}
              <Field label="How often would you need service?">
                <Select value={frequency} onValueChange={setFrequency}>
                  <SelectTrigger data-testid="commercial-frequency" className="h-[50px] border-2 border-slate-900 rounded-none px-4 text-sm font-medium shadow-none focus:ring-0 focus:border-emerald-700">
                    <SelectValue placeholder="Pick a cadence" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none border-2 border-slate-900">
                    {FREQUENCIES.map((f) => (
                      <SelectItem key={f} value={f} className="rounded-none">{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              {/* Field 4 - contract length */}
              <Field label="Are you looking for an ongoing contract, or a fixed-term project?">
                <Select value={contractLength} onValueChange={setContractLength}>
                  <SelectTrigger data-testid="commercial-contract-length" className="h-[50px] border-2 border-slate-900 rounded-none px-4 text-sm font-medium shadow-none focus:ring-0 focus:border-emerald-700">
                    <SelectValue placeholder="Pick one" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none border-2 border-slate-900">
                    {CONTRACT_LENGTHS.map((c) => (
                      <SelectItem key={c} value={c} className="rounded-none">{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              {/* Field 5 - site notes */}
              <Field
                label="Anything about the property we should know?"
                hint="(optional · gate codes, loading dock access, on-site contact, etc.)"
              >
                <textarea
                  data-testid="commercial-notes"
                  value={siteNotes}
                  onChange={(e) => setSiteNotes(e.target.value)}
                  rows={4}
                  placeholder="Tell us about access, contacts on site, special requirements…"
                  className={`${inputCls} resize-none`}
                />
              </Field>

              {/* Field 6 - contact info */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <User size={18} weight="bold" className="text-emerald-700" />
                  <div className="text-[11px] font-extrabold tracking-[0.2em] uppercase text-slate-800">
                    Best way to reach you <span className="text-emerald-700">*</span>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Name" required>
                    <input data-testid="commercial-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="Your full name" />
                  </Field>
                  <Field label="Property name" hint="(if applicable)">
                    <input data-testid="commercial-property-name" value={propertyName} onChange={(e) => setPropertyName(e.target.value)} className={inputCls} placeholder="e.g. 1234 King St Tower" />
                  </Field>
                  <Field label="Email" required>
                    <input data-testid="commercial-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="you@property.com" />
                  </Field>
                  <Field label="Phone" required>
                    <input data-testid="commercial-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} placeholder="(416) 555-1234" />
                  </Field>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="commercial-submit"
                className="w-full group inline-flex items-center justify-center gap-3 px-7 py-5 bg-emerald-700 hover:bg-emerald-800 text-white font-bold uppercase tracking-[0.18em] text-sm border-2 border-emerald-700 hover:border-emerald-800 transition-colors disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Get my custom quote"}
                {!submitting && <ArrowRight size={20} weight="bold" className="group-hover:translate-x-1 transition-transform" />}
              </button>

              <div className="flex items-center justify-center gap-3 text-xs text-slate-500">
                <ShieldCheck size={14} weight="fill" className="text-emerald-600" />
                Your information is used only to prepare your quote.
              </div>
            </form>

            <div className="mt-8 text-center text-sm text-slate-600">
              Prefer to talk? Call{" "}
              <a href={`tel:${COMPANY.phoneRaw}`} className="inline-flex items-center gap-1 font-bold text-emerald-700">
                <Phone size={14} weight="bold" /> {COMPANY.phone}
              </a>
            </div>
          </>
        )}
      </div>

      <Toaster position="top-center" richColors />
    </div>
  );
}
